/* eslint-disable @typescript-eslint/ban-ts-comment */
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { Input } from 'components/Input/Input'
import { ISelectValue } from 'constants/globalTypes'
import { getSingleCycle } from 'features/cycles/cyclesSlice'
import { components } from 'pages/TaskTemplates/taskData'
import React from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Flex, ItemsGrid, ThinSeparator } from 'styling/GlobalStyles'
import { ICycleSingleResponse } from 'types/api/taskCyclesApi.interface'
import { IPhaseState, ITaskCycleBindingConfigState } from 'types/operationPlans.interface'

type CycleTemplateTypeProps = {
    formKey: string | number
    addingPhase: IPhaseState<ITaskCycleBindingConfigState>
    setAddingPhase: React.Dispatch<React.SetStateAction<IPhaseState<ITaskCycleBindingConfigState>>>
}

export const CycleTemplateType = ({ formKey, addingPhase, setAddingPhase }: CycleTemplateTypeProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const bindingConfig = addingPhase.bindingConfig ?? ({} as ITaskCycleBindingConfigState)

    const parse = (data: any) => {
        return data ? data.data.map((item: any) => ({ label: item.name, value: item.id })) : []
    }

    const updateBindingConfig = (patch: Partial<ITaskCycleBindingConfigState>) => {
        setAddingPhase((prev) => ({
            ...prev,
            bindingConfig: { ...(prev.bindingConfig ?? {}), ...patch } as ITaskCycleBindingConfigState,
        }))
    }

    const handleCycleSelect = async (value: ISelectValue) => {
        const resp = await dispatch(getSingleCycle(value.value.toString()))
        if (resp.meta.requestStatus === 'fulfilled') {
            setAddingPhase((prev) => ({
                ...prev,
                taskCycleTemplate: { label: value.label, value: value.value },
                bindingConfig: {
                    startDate: null,
                    parallelismTarget: null,
                    componentsData: (resp.payload as ICycleSingleResponse).components.map((component) => ({
                        taskCycleTemplateComponent: { label: component.name, value: component.id },
                        componentType: component.component,
                        value: null,
                    })),
                },
            }))
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateBindingConfig({ [e.target.name]: e.target.value } as Partial<ITaskCycleBindingConfigState>)
    }

    const handleComponentInput = (e: React.ChangeEvent<HTMLInputElement>, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        const newValue = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.taskCycleTemplateComponent?.value === componentId ? { ...c, value: newValue } : c,
            ),
        })
    }

    const handleComponentSelect = (value: ISelectValue, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.taskCycleTemplateComponent?.value === componentId ? { ...c, value } : c,
            ),
        })
    }

    const handleComponentDate = (date: Date, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.taskCycleTemplateComponent?.value === componentId ? { ...c, value: date } : c,
            ),
        })
    }

    const renderComponentInput = (component: IComponent) => {
        const foundRenderInput = components.find(c => c.component === component.componentType)

        let value
        const componentId = component.taskCycleTemplateComponent?.value
        const fn = foundRenderInput?.inputType == 3 ? handleComponentDate : [4, 5].includes(foundRenderInput?.inputType) ? handleComponentSelect : handleComponentInput

        switch (foundRenderInput?.inputType) {
            case 1:
                value = component.value
                break
            case 2:
                value = Number(component.value)
                break
            case 3:
                value = new Date(String(component.value))
                break
            case 4:
                value = (component.value as ISelectValue)?.value
                break
            case 5:
                value = (component.value as ISelectValue)?.label
        }

        return foundRenderInput?.renderInput({ handleChange: (e) => fn(e, componentId), value, name: 'value', labelText: component?.taskCycleTemplateComponent?.label })
    }

    return <>
        <Flex column gap='1rem' >
            <HonestSelectAsyncInput
                key={`cycle-template-${formKey}`}
                keyProp={1}
                defaultValue={addingPhase.taskCycleTemplate?.label}
                link='task/cycle/template'
                labelText='Task Cycle Template'
                onChange={handleCycleSelect}
                name='taskCycleTemplate'
                placeholder='Search Task Cycles'
                parserFunction={parse}
                required
                invalid={!addingPhase.taskCycleTemplate?.value}
            />
            {addingPhase.taskCycleTemplate?.value && <>
                <Flex gap='1rem' >
                    <CalendarInput
                        key={`cycle-startDate-${formKey}`}
                        labelText='Start Date'
                        minDate={new Date()}
                        name='startDate'
                        defaultValue={bindingConfig.startDate ?? null}
                        onChange={(date) => updateBindingConfig({ startDate: date })}
                        width='100%'
                    />
                    <Input
                        width='100%'
                        value={bindingConfig.parallelismTarget ?? ''}
                        placeholder='Enter parallelism target'
                        labelText='Parallelism Target'
                        name='parallelismTarget'
                        onChange={handleChange}
                        nomarg
                        type='number'
                    />
                </Flex>
                <ThinSeparator />
                <ItemsGrid columns={2} >
                    {bindingConfig.componentsData?.map((c) => renderComponentInput(c))}
                </ItemsGrid>
            </>}
        </Flex>

    </>
}


interface IComponent {
    taskCycleTemplateComponent: ISelectValue;
    componentType: number;
    value: number | Date | ISelectValue | string
}
